import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    IconButton,
    useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { useThemeContext } from '../context/ThemeContext';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';


const originalCardData = [
    { title: 'E-commerce Website', description: 'Custom storefronts built for performance and scale.' },
    { title: 'Mobile Apps', description: 'Cross-platform apps for iOS and Android users.' },
    { title: 'CRM Solutions', description: 'Manage customers efficiently with powerful CRM dashboards.' },
    { title: 'Marketing Tools', description: 'Integrate digital campaigns with smart automation.' },
    { title: 'AI Assistants', description: 'Next-gen intelligent assistants powered by AI.' },
    { title: 'Analytics Dashboards', description: 'Custom dashboards for business insights.' },
];

export default function Home() {
    const navigate = useNavigate();
    const { theme } = useThemeContext();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const visibleCardCount = isMobile ? 2 : 4;
    const extendedCards = [
        ...originalCardData.slice(-visibleCardCount), // clones at start
        ...originalCardData,
        ...originalCardData.slice(0, visibleCardCount), // clones at end
    ];

    const [index, setIndex] = useState(visibleCardCount); // Start from real content

    const heroImages = [
        '/home_page1.jpeg',
        '/home_page2.jpeg',];

    const [currentSlide, setCurrentSlide] = useState(0);
    const timerRef = useRef();
    const heroRef = useRef();

    // Auto scroll
    useEffect(() => {
        timerRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroImages.length);
        }, 3000);
        return () => clearInterval(timerRef.current);
    }, []);

    const trackRef = useRef();

    useEffect(() => {
        const total = extendedCards.length;
        const realStart = visibleCardCount;
        const realEnd = total - visibleCardCount;
        const transitionTime = 500;

        if (trackRef.current) {
            requestAnimationFrame(() => {
                trackRef.current.style.transition = 'transform 0.5s ease-in-out';
                trackRef.current.style.transform = `translateX(-${(index * 100) / total}%)`;
            });
        }

        const timeout = setTimeout(() => {
            if (index === 0) {
                trackRef.current.style.transition = 'none';
                setIndex(realEnd - 1);
            } else if (index === realEnd) {
                trackRef.current.style.transition = 'none';
                setIndex(realStart);
            }
        }, transitionTime);

        return () => clearTimeout(timeout);
    }, [index, extendedCards.length, visibleCardCount]);

    const NextArrow = (props) => {
        const { onClick } = props;
        return (
            <IconButton
                onClick={onClick}
                sx={{
                    color: '#fff',
                    position: 'absolute',
                    right: 0,
                    top: '40%',
                    zIndex: 10,
                }}
            >
                <ArrowForwardIos />
            </IconButton>
        );
    };

    const PrevArrow = (props) => {
        const { onClick } = props;
        return (
            <IconButton
                onClick={onClick}
                sx={{
                    color: '#fff',
                    position: 'absolute',
                    left: 0,
                    top: '40%',
                    zIndex: 10,
                }}
            >
                <ArrowBackIos />
            </IconButton>
        );
    };

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: isMobile ? 2 : 4,
        slidesToScroll: 1,
        arrows: true,
        autoplay: true,
        autoplaySpeed: 4000,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
    };


    return (
        <Box sx={{ backgroundColor: theme.background, color: theme.text }}>
            {/* Hero Section */}
            <Box
                ref={heroRef}
                sx={{
                    position: 'relative',
                    height: '100vh',
                    overflow: 'hidden',
                }}
            >
                {/* Images */}
                {heroImages.map((img, index) => (
                    <Box
                        key={index}
                        sx={{
                            backgroundImage: `url(${img})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            opacity: index === currentSlide ? 1 : 0,
                            transition: 'opacity 1s ease-in-out',
                            zIndex: 0,
                            willChange: 'opacity',
                        }}
                    />
                ))}

                {/* Overlay */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'rgba(0, 0, 0, 0.4)',
                        zIndex: 1,
                    }}
                />

                {/* Content */}
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        px: 2,
                        textAlign: 'center',
                    }}
                >
                    <Typography
                        variant="h2"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '2.2rem', sm: '3rem', md: '4rem' },
                            color: '#ffffff',
                            textShadow: '2px 2px 10px rgba(0,0,0,0.7)',
                        }}
                    >
                        Welcome to House of Technology
                    </Typography>

                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontSize: { xs: '1.1rem', sm: '1.4rem', md: '1.6rem' },
                            color: '#ffffff',
                            fontWeight: 400,
                            textShadow: '1px 1px 6px rgba(0,0,0,0.6)',
                        }}
                    >
                        Building future-ready digital solutions — Web, Apps, CRM & Beyond.
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => navigate('/contact')}
                        sx={{
                            mt: 3,
                            borderRadius: '999px',
                            px: 4,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        }}
                    >
                        Let’s Talk
                    </Button>

                </Box>
            </Box>


            {/* Intro Section */}
            <Container maxWidth="md" sx={{ py: 6 }}>
                <Typography variant="h4" gutterBottom textAlign="center">
                    Empowering Businesses through Innovation
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                    From startups to enterprises, we create impactful digital products that drive business growth and enhance user experience.
                </Typography>
            </Container>

            {/* Carousel Section */}
            <Container maxWidth="xl" sx={{ py: 6, position: 'relative' }}>
                <Typography variant="h4" gutterBottom textAlign="center">
                    What We Can Achieve
                </Typography>

                <Slider {...sliderSettings}>
                    {originalCardData.map((card, idx) => (
                        <Box
                            key={idx}
                            px={1}
                            sx={{
                                minHeight: 200,
                                transition: 'transform 0.5s ease-in-out',
                            }}
                        >
                            <Card
                                sx={{
                                    height: 200, display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    backgroundColor: '#1e1e1e',
                                    color: '#fff',
                                    '&:hover': {
                                        boxShadow: '0 0 35px rgba(255, 255, 255, 0.5)',
                                        border: '2px solid #ffffff55',
                                    },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                    }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.4rem' },
                                            fontWeight: 700,
                                            mb: 1,
                                        }}
                                    >
                                        {card.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                            color: 'rgba(255,255,255,0.7)',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                        }}
                                    >
                                        {card.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    ))}
                </Slider>

            </Container>

            <Footer />
        </Box>
    );
}
